import { TextField } from "@/app/fields";
import { RowActions } from "@/app/RowActions";
import type { Errors } from "@/app/useDraft";
import { Button, Combobox, EditList, EditRow, Field, Form, Input, Switch, Textarea, type ComboOption } from "@/ui";
import { newMapping, newPattern, senderPolicyRowErrors, type SenderPolicyDraft } from "./senderPolicyDraft";

interface Props {
  draft: SenderPolicyDraft; errors: Errors<SenderPolicyDraft>; set: <K extends keyof SenderPolicyDraft>(k: K, v: SenderPolicyDraft[K]) => void;
  isNew: boolean; accounts: ComboOption[]; channels: ComboOption[];
}

// 映射只删不排（精确匹配、源不重复，顺序无关）；正则按顺序试，可上移下移（D154 第 2 条）
const removeOnly = "var(--control-h-sm)";
const movable = "calc(3 * var(--control-h-sm) + 2 * var(--space-1))";
const MAPPING_COLUMNS = `minmax(0, 1fr) minmax(0, 1fr) ${removeOnly}`;
const PATTERN_COLUMNS = `1.5em minmax(0, 1.4fr) minmax(0, 1fr) ${movable}`;

/** SID 替换策略的字段（决策 D154）：条件、精确映射、正则、默认替换。条件为空表示不限。 */
export function SenderPolicyForm({ draft, errors, set, isNew, accounts, channels }: Props) {
  const rows = senderPolicyRowErrors(draft);
  return (
    <Form>
      <TextField id="sp-name" label="名称" required value={draft.name} error={errors.name} onChange={(v) => set("name", v)} />
      <TextField id="sp-priority" label="优先级" required mono value={draft.priority} error={errors.priority} hint="数字越小越优先，不能重复；只取第一条条件全部满足的策略" onChange={(v) => set("priority", v.trim())} />
      <Field label="账号" hint="不选表示所有账号">
        <Combobox multiple block options={accounts} value={draft.account_ids} placeholder="所有账号" ariaLabel="账号条件" onChange={(v) => set("account_ids", v)} />
      </Field>
      <TextField id="sp-mcc" label="MCC" mono placeholder="不限" value={draft.mcc} error={errors.mcc} onChange={(v) => set("mcc", v.trim())} />
      <TextField id="sp-mnc" label="MNC" mono placeholder="不限" value={draft.mnc} error={errors.mnc} hint="要配合 MCC 使用" onChange={(v) => set("mnc", v.trim())} />
      <Field label="通道" hint="不选表示所有通道；按每次实际发送的通道判断，失败重试换通道时重新匹配">
        <Combobox multiple block options={channels} value={draft.channel_ids} placeholder="所有通道" ariaLabel="通道条件" onChange={(v) => set("channel_ids", v)} />
      </Field>

      <Field label="精确映射" error={errors.mappings} hint="原 sender 不区分大小写；先于正则与默认替换。目标留空表示发空 sender，由上游填默认值">
        <EditList label="精确映射" columns={MAPPING_COLUMNS} headers={["原 sender", "替换为", ""]}>
          {draft.mappings.map((m, i) => (
            <EditRow key={m.key} label={`第 ${i + 1} 条映射`} error={rows.mappings[i]}
              actions={<RowActions movable={false} rows={draft.mappings} index={i} label={`第 ${i + 1} 条映射`} onChange={(v) => set("mappings", v)} />}>
              <Input size="sm" mono aria-label={`第 ${i + 1} 条映射的原 sender`} value={m.from} onChange={(e) => set("mappings", draft.mappings.map((x) => (x.key === m.key ? { ...x, from: e.target.value } : x)))} />
              <Input size="sm" mono aria-label={`第 ${i + 1} 条映射的目标`} placeholder="（空）" value={m.to} onChange={(e) => set("mappings", draft.mappings.map((x) => (x.key === m.key ? { ...x, to: e.target.value } : x)))} />
            </EditRow>
          ))}
        </EditList>
      </Field>
      <div><Button size="sm" onClick={() => set("mappings", [...draft.mappings, newMapping()])}>添加映射</Button></div>

      <Field label="正则替换" error={errors.patterns} hint="整串匹配，按顺序试第一条命中的；目标可用 ${1} 引用分组，展开后不合法时原样发送">
        <EditList label="正则替换" columns={PATTERN_COLUMNS} headers={["#", "正则", "替换为", ""]} ordered>
          {draft.patterns.map((p, i) => (
            <EditRow key={p.key} order={i + 1} label={`第 ${i + 1} 条正则`} error={rows.patterns[i]}
              actions={<RowActions rows={draft.patterns} index={i} label={`第 ${i + 1} 条正则`} onChange={(v) => set("patterns", v)} />}>
              <Input size="sm" mono aria-label={`第 ${i + 1} 条正则`} value={p.pattern} onChange={(e) => set("patterns", draft.patterns.map((x) => (x.key === p.key ? { ...x, pattern: e.target.value } : x)))} />
              <Input size="sm" mono aria-label={`第 ${i + 1} 条正则的目标`} placeholder="（空）" value={p.to} onChange={(e) => set("patterns", draft.patterns.map((x) => (x.key === p.key ? { ...x, to: e.target.value } : x)))} />
            </EditRow>
          ))}
        </EditList>
      </Field>
      <div><Button size="sm" onClick={() => set("patterns", [...draft.patterns, newPattern()])}>添加正则</Button></div>

      <Field label="默认替换" error={errors.default_enabled} hint="映射与正则都没命中时，所有 sender 都换成目标；不开启则原样发送">
        <Switch label="开启默认替换" checked={draft.default_enabled} onChange={(on) => set("default_enabled", on)} />
      </Field>
      {draft.default_enabled && (
        <TextField id="sp-default" label="默认目标" mono placeholder="（空）" value={draft.default_target} error={errors.default_target} hint="留空表示发空 sender，由上游填默认值" onChange={(v) => set("default_target", v)} />
      )}
      <Field label="备注" htmlFor="sp-note"><Textarea id="sp-note" rows={2} value={draft.note} onChange={(e) => set("note", e.target.value)} /></Field>
      {!isNew && <Switch label="启用" checked={draft.status === "active"} onChange={(on) => set("status", on ? "active" : "disabled")} />}
    </Form>
  );
}
